import { FetchSheetQuery } from '../generated/FetchSheetQuery';
import { GenerateJSON } from '../generated/GenerateJSON';
import { GenerateDBO } from '../generated/GenerateDBO';
import { parseDate } from './dateParser';

export function normalizeSheetData(data: FetchSheetQuery) {
  let projectName = data.fetchSheet.projectName;
  let estimatedDeliveryDate = new Date(data.fetchSheet.estimatedDeliveryDate);
  let { day: deliveryDate, month, year } = parseDate(estimatedDeliveryDate, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  let projectStartDate = new Date(data.fetchSheet.projectStartDate);
  let { day: startDate, month: startMonth, year: startYear } = parseDate(
    projectStartDate,
    {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    },
  );
  let barChartData = [
    {
      key: 'Original Estimate',
      value: data.fetchSheet.originalFeatures.totalDays,
      hoverContent: {
        message:
          'This represents the percentage of development time which was prior to any changes, additions, or adjustments to the scope and timeline',
      },
    },
    {
      key: 'Timeline Projection Adjustments',
      value: data.fetchSheet.overdueFeatures.totalDays,
      hoverContent: {
        message: 'Our timeline estimates are subject to a variance of +/- 15%',
      },
    },
    {
      key: 'Feature Expansion/Additions or Delays',
      value: data.fetchSheet.additionalFeatures.totalDays,
      hoverContent: {
        message:
          'These adjustments come from changes or additions to the scope of work at the request of the stakeholder, or delays outside the control of KodeFox',
      },
    },
  ];

  let addCommas = (num: number) => {
    return num.toLocaleString('en');
  };

  let popoverData = [
    {
      title: 'Original Estimate',
      details: `${
        data.fetchSheet.originalFeatures.totalDays
      } mandays (~$${addCommas(data.fetchSheet.originalFeatures.price)})`,
      data: data.fetchSheet.originalFeatures.features,
    },
    {
      title: 'Timeline Projection Adjustments',
      details: `${
        data.fetchSheet.overdueFeatures.totalDays
      } mandays (~$${addCommas(data.fetchSheet.overdueFeatures.price)})`,
      data: data.fetchSheet.overdueFeatures.features,
    },
    {
      title: 'Feature Expansion/Additions or Delays',
      details: `${
        data.fetchSheet.additionalFeatures.totalDays
      } mandays (~$${addCommas(data.fetchSheet.additionalFeatures.price)})`,
      data: data.fetchSheet.additionalFeatures.features,
    },
  ];
  let billData = [
    {
      key: 'Paid',
      value: data.fetchSheet.billing.paidInvoices,
      currency: data.fetchSheet.currency,
    },
    {
      key: 'Outstanding',
      value: data.fetchSheet.billing.outstandingInvoices,
      currency: data.fetchSheet.currency,
    },
    {
      key: 'Est. Remaining Cost',
      value: data.fetchSheet.billing.remainingInvoices,
      currency: data.fetchSheet.currency,
    },
  ];
  let totalMandays = data.fetchSheet.totalMandays;
  let projectDaysRemaining = data.fetchSheet.projectDaysRemaining;

  return {
    estimatedDeliveryDate,
    deliveryDate: deliveryDate !== 'Invalid Date' ? deliveryDate : null,
    deliveryMonthYearDesc:
      month !== 'Invalid Date' && year !== 'Invalid Date'
        ? `${month} ${year}`
        : 'Invalid Date',
    startDate,
    startMonth,
    startYear,
    barChartData,
    popoverData,
    billData,
    projectName,
    totalMandays,
    projectDaysRemaining,
  };
}

export function normalizeJsonCSV(jsonData: GenerateJSON) {
  return jsonData.generateJSON.detail.map((data) => {
    let {
      featureName,
      description,
      resources,
      mandaysSpent,
      startDate,
      finishDate,
    } = data;
    let parsedStartDate = parseDate(new Date(startDate), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    let parsedFinishDate = parseDate(new Date(finishDate), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

    let finalStartDate =
      startDate === ' - '
        ? startDate
        : `${parsedStartDate.day} ${parsedStartDate.month} ${parsedStartDate.year}`;
    let finalFinishDate =
      finishDate === ' - '
        ? finishDate
        : `${parsedFinishDate.day} ${parsedFinishDate.month} ${parsedFinishDate.year}`;

    return {
      featureName,
      description,
      resources,
      mandaysSpent,
      startDate: finalStartDate,
      finishDate: finalFinishDate,
    };
  });
}

export function normalizeDBOCSV(jsonData: GenerateDBO) {
  return jsonData.generateDBO.detail.map((data) => {
    let { week, days, date, name, resource, task, mandays } = data;
    return {
      week,
      days,
      date,
      name,
      resource,
      task,
      mandays,
    };
  });
}
